import { CORE_PHYSICS, GAME_FRAME } from "../../config/gameplay";
import { createCoreFromLaunch } from "../cores/factory";
import { lookupProfileProgress } from "../progression/levels";
import type { OrbitForgeSave } from "../save/types";
import { getUpgradeDefinition, type UpgradeKind } from "../upgrades/definitions";
import type { UpgradeEffects } from "../upgrades/effects";
import { calculateRollbackRefund, calculateUpgradePrice } from "../upgrades/pricing";
import type { CoreState } from "./types";
import type { FeedbackDraft } from "./feedback";
import { upgradeFeedbackTargets } from "./upgrade-feedback";

export interface LaunchCommandInput {
  readonly save: OrbitForgeSave;
  readonly cores: readonly CoreState[];
  readonly effects: UpgradeEffects;
  readonly frameX: number;
  readonly frameY: number;
  readonly manualCooldownSeconds: number;
}

export interface LaunchCommandResult {
  readonly cores: readonly CoreState[];
  readonly manualCooldownSeconds: number;
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export interface UpgradeCommandResult {
  readonly dirty: boolean;
  readonly rebuildShells: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export function runLaunchCoreCommand(input: LaunchCommandInput): LaunchCommandResult {
  const activeProgressCores = input.cores.filter((core) => core.countsAgainstLaunchLimit).length;
  if (
    input.manualCooldownSeconds > 0 ||
    activeProgressCores >= input.effects.maxCores ||
    input.cores.length >= CORE_PHYSICS.hardActiveCoreCap
  ) {
    return {
      cores: input.cores,
      manualCooldownSeconds: input.manualCooldownSeconds,
      dirty: false,
      feedback: [{ kind: "denied", target: "active-cores", frameX: input.frameX, frameY: input.frameY }]
    };
  }

  const activeOverchargedCores = input.cores.filter((core) => core.isOvercharged && core.countsAsProgress).length;
  const result = createCoreFromLaunch(input.save, input.effects, {
    activeOverchargedCores
  });
  input.save.rngState = result.nextRngState;
  input.save.nextEntityId += 1;
  input.save.stats.TotalCoresLaunched += 1;
  input.save.stats.DailyCoresLaunched += 1;
  input.save.stats.WeeklyCoresLaunched += 1;
  if (result.core.isOvercharged) {
    input.save.stats.OverchargedCoresLaunched += 1;
  }

  const spawnFrameX = result.core.position.x + GAME_FRAME.width / 2;
  const spawnFrameY = result.core.position.y + GAME_FRAME.height / 2;
  const feedback: FeedbackDraft[] = [{ kind: "core-spawn", frameX: spawnFrameX, frameY: spawnFrameY, color: result.core.color }];
  if (result.core.isOvercharged) {
    feedback.push({
      kind: "rare-core",
      frameX: spawnFrameX,
      frameY: spawnFrameY - 34,
      color: "gold",
      text: `RARE x${result.core.rewardMultiplier}`
    });
  }

  return {
    cores: [...input.cores, result.core],
    manualCooldownSeconds: CORE_PHYSICS.manualCooldownSeconds,
    dirty: true,
    feedback
  };
}

export function runBuyUpgradeCommand(save: OrbitForgeSave, kind: UpgradeKind): UpgradeCommandResult {
  const progress = lookupProfileProgress(save.stats);
  const definition = getUpgradeDefinition(kind);
  const level = save.upgrades[kind];
  const price = calculateUpgradePrice(definition, level);

  if (progress.effectiveLevel < definition.unlockLevel || level >= definition.maxLevel || save.stats.Credits < price) {
    return {
      dirty: false,
      rebuildShells: false,
      feedback: [{ kind: "denied", target: `upgrade-${kind}` }]
    };
  }

  save.stats.Credits -= price;
  save.upgrades[kind] = level + 1;

  return {
    dirty: true,
    rebuildShells: true,
    feedback: upgradeCommandFeedback(kind)
  };
}

export function runRollbackUpgradeCommand(save: OrbitForgeSave, kind: UpgradeKind): UpgradeCommandResult {
  const progress = lookupProfileProgress(save.stats);
  const definition = getUpgradeDefinition(kind);
  const level = save.upgrades[kind];

  if (progress.effectiveLevel < 5 || level <= 0) {
    return {
      dirty: false,
      rebuildShells: false,
      feedback: [{ kind: "denied", target: `upgrade-${kind}` }]
    };
  }

  save.upgrades[kind] = level - 1;
  save.stats.Credits += calculateRollbackRefund(definition, level);

  return {
    dirty: true,
    rebuildShells: true,
    feedback: upgradeCommandFeedback(kind)
  };
}

function upgradeCommandFeedback(kind: UpgradeKind): readonly FeedbackDraft[] {
  return [{ kind: "pulse", target: `upgrade-${kind}` }, ...upgradeFeedbackTargets(kind).map((target) => ({ kind: "pulse" as const, target }))];
}
